package com.iptvplayer.shared.db.shared

import app.cash.sqldelight.TransacterImpl
import app.cash.sqldelight.db.AfterVersion
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlDriver
import app.cash.sqldelight.db.SqlSchema
import com.iptvplayer.shared.db.CacheMetaQueries
import com.iptvplayer.shared.db.ChannelQueries
import com.iptvplayer.shared.db.FavoriteQueries
import com.iptvplayer.shared.db.IPTVDatabase
import com.iptvplayer.shared.db.IptvSourceQueries
import com.iptvplayer.shared.db.SeriesQueries
import com.iptvplayer.shared.db.VodItemQueries
import com.iptvplayer.shared.db.WatchHistoryQueries
import kotlin.Long
import kotlin.Unit
import kotlin.reflect.KClass

internal val KClass<IPTVDatabase>.schema: SqlSchema<QueryResult.Value<Unit>>
  get() = IPTVDatabaseImpl.Schema

internal fun KClass<IPTVDatabase>.newInstance(driver: SqlDriver): IPTVDatabase =
    IPTVDatabaseImpl(driver)

private class IPTVDatabaseImpl(
  driver: SqlDriver,
) : TransacterImpl(driver), IPTVDatabase {
  override val cacheMetaQueries: CacheMetaQueries = CacheMetaQueries(driver)

  override val channelQueries: ChannelQueries = ChannelQueries(driver)

  override val favoriteQueries: FavoriteQueries = FavoriteQueries(driver)

  override val iptvSourceQueries: IptvSourceQueries = IptvSourceQueries(driver)

  override val seriesQueries: SeriesQueries = SeriesQueries(driver)

  override val vodItemQueries: VodItemQueries = VodItemQueries(driver)

  override val watchHistoryQueries: WatchHistoryQueries = WatchHistoryQueries(driver)

  public object Schema : SqlSchema<QueryResult.Value<Unit>> {
    override val version: Long
      get() = 2

    override fun create(driver: SqlDriver): QueryResult.Value<Unit> {
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS CacheMeta (
          |    key TEXT NOT NULL PRIMARY KEY,
          |    value TEXT NOT NULL
          |)
          """.trimMargin(), 0)
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS ChannelEntity (
          |    id TEXT NOT NULL PRIMARY KEY,
          |    sourceId TEXT NOT NULL,
          |    name TEXT NOT NULL,
          |    streamUrl TEXT NOT NULL,
          |    logoUrl TEXT,
          |    groupName TEXT,
          |    streamType TEXT NOT NULL DEFAULT 'HLS'
          |)
          """.trimMargin(), 0)
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS FavoriteEntity (
          |    id TEXT NOT NULL PRIMARY KEY,
          |    userId TEXT NOT NULL,
          |    contentId TEXT NOT NULL,
          |    contentType TEXT NOT NULL,
          |    contentName TEXT NOT NULL,
          |    contentLogoUrl TEXT,
          |    streamUrl TEXT NOT NULL,
          |    addedAt INTEGER NOT NULL
          |)
          """.trimMargin(), 0)
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS IptvSourceEntity (
          |    id TEXT NOT NULL PRIMARY KEY,
          |    name TEXT NOT NULL,
          |    type TEXT NOT NULL,
          |    url TEXT,
          |    serverUrl TEXT,
          |    username TEXT,
          |    password TEXT
          |)
          """.trimMargin(), 0)
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS SeriesEntity (
          |    id TEXT NOT NULL PRIMARY KEY,
          |    sourceId TEXT NOT NULL,
          |    seriesId INTEGER NOT NULL,
          |    name TEXT NOT NULL,
          |    posterUrl TEXT,
          |    plot TEXT,
          |    castMembers TEXT,
          |    director TEXT,
          |    genre TEXT,
          |    releaseDate TEXT,
          |    rating REAL,
          |    groupName TEXT
          |)
          """.trimMargin(), 0)
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS VodItemEntity (
          |    id TEXT NOT NULL PRIMARY KEY,
          |    sourceId TEXT NOT NULL,
          |    name TEXT NOT NULL,
          |    streamUrl TEXT NOT NULL,
          |    coverUrl TEXT,
          |    plot TEXT,
          |    genre TEXT,
          |    duration INTEGER,
          |    rating REAL,
          |    groupName TEXT
          |)
          """.trimMargin(), 0)
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS WatchHistoryEntity (
          |    id TEXT NOT NULL PRIMARY KEY,
          |    userId TEXT NOT NULL,
          |    contentId TEXT NOT NULL,
          |    contentType TEXT NOT NULL,
          |    contentName TEXT NOT NULL,
          |    contentLogoUrl TEXT,
          |    streamUrl TEXT NOT NULL,
          |    resumePositionMs INTEGER NOT NULL DEFAULT 0,
          |    durationMs INTEGER,
          |    lastWatchedAt INTEGER NOT NULL
          |)
          """.trimMargin(), 0)
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_channel_group ON ChannelEntity(groupName)", 0)
      driver.execute(null,
          "CREATE INDEX IF NOT EXISTS idx_channel_source ON ChannelEntity(sourceId)", 0)
      driver.execute(null, "CREATE INDEX IF NOT EXISTS idx_channel_name ON ChannelEntity(name)", 0)
      driver.execute(null, "CREATE INDEX IF NOT EXISTS idx_series_group ON SeriesEntity(groupName)",
          0)
      driver.execute(null, "CREATE INDEX IF NOT EXISTS idx_series_source ON SeriesEntity(sourceId)",
          0)
      driver.execute(null, "CREATE INDEX IF NOT EXISTS idx_series_name ON SeriesEntity(name)", 0)
      driver.execute(null, "CREATE INDEX IF NOT EXISTS idx_vod_group ON VodItemEntity(groupName)",
          0)
      driver.execute(null, "CREATE INDEX IF NOT EXISTS idx_vod_source ON VodItemEntity(sourceId)",
          0)
      driver.execute(null, "CREATE INDEX IF NOT EXISTS idx_vod_name ON VodItemEntity(name)", 0)
      return QueryResult.Unit
    }

    private fun migrateInternal(
      driver: SqlDriver,
      oldVersion: Long,
      newVersion: Long,
    ): QueryResult.Value<Unit> {
      if (oldVersion <= 1 && newVersion > 1) {
        driver.execute(null,
            "CREATE INDEX IF NOT EXISTS idx_channel_group ON ChannelEntity(groupName)", 0)
        driver.execute(null,
            "CREATE INDEX IF NOT EXISTS idx_channel_source ON ChannelEntity(sourceId)", 0)
        driver.execute(null, "CREATE INDEX IF NOT EXISTS idx_channel_name ON ChannelEntity(name)",
            0)
        driver.execute(null, "CREATE INDEX IF NOT EXISTS idx_vod_group ON VodItemEntity(groupName)",
            0)
        driver.execute(null, "CREATE INDEX IF NOT EXISTS idx_vod_source ON VodItemEntity(sourceId)",
            0)
        driver.execute(null, "CREATE INDEX IF NOT EXISTS idx_vod_name ON VodItemEntity(name)", 0)
        driver.execute(null, """
            |CREATE TABLE IF NOT EXISTS SeriesEntity (
            |    id TEXT NOT NULL PRIMARY KEY,
            |    sourceId TEXT NOT NULL,
            |    seriesId INTEGER NOT NULL,
            |    name TEXT NOT NULL,
            |    posterUrl TEXT,
            |    plot TEXT,
            |    castMembers TEXT,
            |    director TEXT,
            |    genre TEXT,
            |    releaseDate TEXT,
            |    rating REAL,
            |    groupName TEXT
            |)
            """.trimMargin(), 0)
        driver.execute(null,
            "CREATE INDEX IF NOT EXISTS idx_series_group ON SeriesEntity(groupName)", 0)
        driver.execute(null,
            "CREATE INDEX IF NOT EXISTS idx_series_source ON SeriesEntity(sourceId)", 0)
        driver.execute(null, "CREATE INDEX IF NOT EXISTS idx_series_name ON SeriesEntity(name)", 0)
      }
      return QueryResult.Unit
    }

    override fun migrate(
      driver: SqlDriver,
      oldVersion: Long,
      newVersion: Long,
      vararg callbacks: AfterVersion,
    ): QueryResult.Value<Unit> {
      var lastVersion = oldVersion

      callbacks.filter { it.afterVersion in oldVersion until newVersion }
      .sortedBy { it.afterVersion }
      .forEach { callback ->
        migrateInternal(driver, oldVersion = lastVersion, newVersion = callback.afterVersion + 1)
        callback.block(driver)
        lastVersion = callback.afterVersion + 1
      }

      if (lastVersion < newVersion) {
        migrateInternal(driver, lastVersion, newVersion)
      }
      return QueryResult.Unit
    }
  }
}
